import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllCampaignsReport.graphql';
import { Query, QueryallCampaignsReportArgs } from '../../__generated__/globalTypes';

export const useAllCampaignsReportQuery = (options?: QueryHookOptions<Query, QueryallCampaignsReportArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallCampaignsReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllCampaignsReportLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallCampaignsReportArgs>
): QueryResult<Query, QueryallCampaignsReportArgs> & {
  getAllCampaignsReport: LazyQueryExecFunction<Query, QueryallCampaignsReportArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllCampaignsReport, result] = useLazyQuery<Query, QueryallCampaignsReportArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllCampaignsReport, ...result };
};

export const useAllCampaignsReportPromiseQuery = () => {
  const client = useApolloClient();

  const getAllCampaignsReport = useCallback(
    (options?: Omit<QueryOptions<QueryallCampaignsReportArgs, Query>, 'query'>) =>
      client.query<Query, QueryallCampaignsReportArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllCampaignsReport };
};
// Suspense Query Hooks
export const useAllCampaignsReportSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallCampaignsReportArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallCampaignsReportArgs>(QUERY, {
    ...options
  });

  return result;
};
